import { z } from 'zod'

import { JEPX_VOLUME_CONSTRAINT } from '~/models/numberInputConstraint'
import { emissionRefine } from '~/pages/SpotBidding/models/schema'

/**
 * JEPX自由記述欄 (メモ〈JEPX提出用〉) の文字数上限。 eSLJ へ note として送信されるため、
 * 送信先 eSLJ (day_ahead.v1 CreateBidInput.note) の 70 文字上限に合わせる
 * (通常入札の memoJepx と同じ制約)。 入力欄の maxLength・文字数カウンタもこの値を使う。
 */
export const BLOCK_MEMO_JEPX_MAX_LEN = 70

/** メモ（自由記述、 社内限定で JEPX へは送信しない）の文字数上限。 */
export const BLOCK_MEMO_FREE_MAX_LEN = 100

const blockLegFormSchema = z.object({
  areaCode: z
    .number({ error: 'エリアを選択してください' })
    .int()
    .min(1, 'エリアを選択してください'),
  direction: z
    .number({ error: '売/買を選択してください' })
    .int()
    .min(1, '売/買を選択してください'),
  /**
   * 「全コマ一括」の入札量 (MW)。 コマ範囲内の全コマに [[slotVolumes]] の上書きが
   * あれば提出値に使われないので、 空でも値域外でも通す。 上書きの無いコマが 1 つでも
   * 残っていれば必須・値域を検証する ([[baseVolumeCheck]] 参照)。
   */
  volumeMwh: z
    .preprocess(
      // 入力欄は `valueAsNumber` (他の数値欄と同じ) なので、 空欄は NaN で届く。
      // NaN = 未入力として undefined に畳む。 必須・値域は [[baseVolumeCheck]] が
      // コマ範囲と上書きから判定する (提出値に使われる時だけ検証するため)。
      (v: number | undefined) =>
        v !== undefined && Number.isNaN(v) ? undefined : v,
      z.number().optional(),
    )
    // 外側の optional は form 側の入力型 (useForm の resolver) でもキーを省略可にするため。
    .optional(),
  /**
   * コマ別の入札量上書き (絶対コマ番号 1〜48 → MW)。 未指定 / 空配列 = 全コマが
   * [[volumeMwh]] (=「全コマ一括」の既定値)。
   *
   * ブロック入札は JEPX 制度上コマ毎に異なる入札量を指定できる (AC-BSP-030a。
   * JEPX 取引ガイド p.8「時間帯毎の量、 加重平均価格を指定して入札します」)。
   * 単一値 + 上書きの 2 層モデルにするのは design 正本 `design/bidding/spot.html` の
   * `_volState = { volume, slotVolumes }` に合わせるため (2026-08-18 Des 裁定)。
   *
   * キーを **絶対コマ番号**にしているのはコマ範囲を動かしても範囲外の上書きを保持する
   * ため (= 範囲を戻すと元の値が復活する)。 提出時は範囲内のコマだけを採用する
   * ([[legToBlockBidInput]])。
   *
   * 値域の検証は要素スキーマではなく下の superRefine で **コマ範囲内のコマだけ**に
   * 対して行う ([[validateSlotVolumes]] 参照)。
   */
  slotVolumes: z
    .array(
      z.object({
        timeCode: z.number().int().min(1).max(48),
        volumeMwh: z.number({ error: 'コマ別の入札量を入力してください' }),
      }),
    )
    .optional(),
  priceYenPerKwh: z
    .number({ error: '入札価格を入力してください' })
    // 範囲外の文言はスポット・時間前・自動入札ルールで共通 (AC-BSP-074 / AC-BSP-078)
    .min(0.01, '価格は 0.01 〜 999 円/kWh の範囲で入力してください')
    .max(999, '価格は 0.01 〜 999 円/kWh の範囲で入力してください'),
  timeCodeFrom: z
    .number({ error: '開始コマを選択してください' })
    .int()
    .min(1)
    .max(48),
  timeCodeTo: z
    .number({ error: '終了コマを選択してください' })
    .int()
    .min(1)
    .max(48),
})

export type BlockLegFormInput = z.infer<typeof blockLegFormSchema>

/**
 * ブロック入札の数量欄の値域 (AC-BSP-051)。 連続範囲の中間コマは 0 (= そのコマは
 * 入札しない) を取れるので、 下限だけ通常入札の JEPX_VOLUME_CONSTRAINT と違う。
 * 先頭・末尾コマの 0 と 0.1 未満の端数は [[blockSlotVolumeViolationOf]] が弾く。
 */
export const BLOCK_SLOT_VOLUME_CONSTRAINT = {
  ...JEPX_VOLUME_CONSTRAINT,
  min: 0,
} as const

type BlockSlotVolumeViolation = 'boundaryZero' | 'outOfRange'

const BLOCK_VOLUME_BOUNDARY_ZERO_MESSAGE = '連続範囲の境界コマは 0 にできません'
const BLOCK_VOLUME_RANGE_MESSAGE =
  '入札量は 0.1 〜 9999.9 MW の範囲で入力してください'

/**
 * 値域違反の文言 (AC-BSP-074 / AC-BSP-078)。 base は「全コマ一括」欄、 slot はコマ別の欄に
 * 出す。コマ別の欄でも spec の規定文言をそのまま表示する。
 */
const BLOCK_SLOT_VOLUME_VIOLATION_MESSAGES: Record<
  BlockSlotVolumeViolation,
  { base: string; slot: (timeCode: number) => string }
> = {
  boundaryZero: {
    base: BLOCK_VOLUME_BOUNDARY_ZERO_MESSAGE,
    slot: () => BLOCK_VOLUME_BOUNDARY_ZERO_MESSAGE,
  },
  outOfRange: {
    base: BLOCK_VOLUME_RANGE_MESSAGE,
    slot: () => BLOCK_VOLUME_RANGE_MESSAGE,
  },
}

/**
 * 1 コマの実効入札量の値域違反 (AC-BSP-051 / AC-BSP-074)。 0 を取れるのは連続範囲の
 * 中間コマだけで、 先頭・末尾コマ (isBoundary) の 0 は違反。 0 以外は 0.1〜9999.9 MW。
 * コマ別上書きと「全コマ一括」のどちらで決まった値にも同じ規則を当てる。
 *
 * 先頭・末尾コマの 0 を止めるのは、 画面経路ではこの入力チェックだけ。 画面経路の backend は
 * 拒否しない (AC-BSP-051 の backend 再検証は #8493 で見送り。 CSV 取込は backend が拒否する)。
 */
function blockSlotVolumeViolationOf(
  volumeMw: number,
  isBoundary: boolean,
): BlockSlotVolumeViolation | undefined {
  if (volumeMw === 0) return isBoundary ? 'boundaryZero' : undefined
  if (
    volumeMw < JEPX_VOLUME_CONSTRAINT.min ||
    volumeMw > JEPX_VOLUME_CONSTRAINT.max
  ) {
    return 'outOfRange'
  }
  return undefined
}

function isBoundaryTimeCode(
  leg: Pick<BlockLegFormInput, 'timeCodeFrom' | 'timeCodeTo'>,
  timeCode: number,
): boolean {
  return timeCode === leg.timeCodeFrom || timeCode === leg.timeCodeTo
}

/**
 * 「全コマ一括」値の検証エラー文言 (問題なしは undefined)。 一括値は入力補助で
 * (AC-BSP-030a)、 値域は各コマの数量が受け持つ (AC-BSP-051)。 実効値は上書き優先
 * ([[effectiveSlotVolume]]) なので、 コマ範囲内に上書きの無いコマが残り一括値が提出値に
 * 使われる時だけ、 必須と値域を検証する。 値域は一括値が効くコマ (上書きの無いコマ) ごとに
 * [[blockSlotVolumeViolationOf]] で見るので、 一括値の 0 は効く先が中間コマだけなら通り、
 * 先頭・末尾コマに効く時だけ違反になる。
 */
function baseVolumeIssueOf(
  leg: Pick<
    BlockLegFormInput,
    'volumeMwh' | 'slotVolumes' | 'timeCodeFrom' | 'timeCodeTo'
  >,
): string | undefined {
  const baseSlots = effectiveLegSlotVolumes(leg).filter(
    (slot) => !slot.overridden,
  )
  if (baseSlots.length === 0) return undefined
  const base = leg.volumeMwh
  if (base === undefined || !Number.isFinite(base)) {
    return '入札量を入力してください'
  }
  for (const slot of baseSlots) {
    const violation = blockSlotVolumeViolationOf(
      base,
      isBoundaryTimeCode(leg, slot.timeCode),
    )
    if (violation !== undefined) {
      return BLOCK_SLOT_VOLUME_VIOLATION_MESSAGES[violation].base
    }
  }
  return undefined
}

/**
 * [[baseVolumeIssueOf]] を leg ごとの object refine として掛ける。
 *
 * zod 4 は他の欄に型エラーがあると既定で object の refine を走らせない (= 価格を直すまで
 * この必須エラーが出ない 2 段階表示になる) ので、 `when` で判定の可否を決める。 型エラーで
 * 値が落ちた欄 (volumeMwh / slotVolumes) が絡む leg は 「空」と区別できず誤検出になるため、
 * その欄に既に issue がある leg は判定しない。
 */
function baseVolumeCheck(
  prefix: 'primaryLeg' | 'secondaryLeg',
): [(data: unknown) => boolean, z.core.$ZodCustomParams] {
  const issueOf = (data: unknown): string | undefined => {
    const leg = (
      data as Partial<Record<typeof prefix, Partial<BlockLegFormInput>>>
    )[prefix]
    if (leg === undefined || typeof leg !== 'object') return undefined
    const { timeCodeFrom, timeCodeTo } = leg
    if (typeof timeCodeFrom !== 'number' || typeof timeCodeTo !== 'number') {
      return undefined
    }
    return baseVolumeIssueOf({
      volumeMwh: leg.volumeMwh,
      slotVolumes: leg.slotVolumes,
      timeCodeFrom,
      timeCodeTo,
    })
  }
  return [
    (data: unknown): boolean => issueOf(data) === undefined,
    {
      error: (issue) => issueOf(issue.input),
      path: [prefix, 'volumeMwh'],
      when: (payload: z.core.ParsePayload) =>
        !payload.issues.some(
          (issue) =>
            issue.path?.[0] === prefix &&
            (issue.path[1] === 'volumeMwh' || issue.path[1] === 'slotVolumes'),
        ),
    },
  ]
}

export const blockBidCreateSchema = z
  .object({
    kind: z.enum(['STANDARD', 'LINK', 'LOOP']),
    /**
     * 発注主体区分 (LicenseType enum number)。 LINK / LOOP は all-or-nothing なので
     * グループ単位で 1 つだけ選び、 選んだ区分をそのまま書込の issuer_type として送る。
     */
    issuerType: z.number().int().min(1, '発注主体を選択してください'),
    primaryLeg: blockLegFormSchema,
    secondaryLeg: blockLegFormSchema.optional(),
    /**
     * JEPX自由記述欄。 ブロック単位で 1 つ (LINK の親子・LOOP の A/B で共通) とし、
     * 保存時に両 Leg へ同じ値を載せる (spec bidding/spot-bidding.md AC-BSP-031/032、
     * Discussion「リンク・ループの JEPX自由記述欄・メモ・排出係数紐付けの持ち方」決定 B)。
     */
    memoJepx: z
      .string()
      .max(
        BLOCK_MEMO_JEPX_MAX_LEN,
        // 上限 70 文字は eSLJ の note の上限に準拠 (AC-BSP-074)
        'JEPX 自由記述欄は70文字以内で入力してください',
      )
      .optional(),
    /** メモ（自由記述）。 [[memoJepx]] と同じくブロック単位で 1 つ。 */
    memoFree: z
      .string()
      .max(BLOCK_MEMO_FREE_MAX_LEN, 'メモは100文字以内で入力してください')
      .optional(),
    /**
     * 排出係数紐付けの ON/OFF と英字 (AC-BSP-083〜085)。 [[memoJepx]] と同じくブロック単位で 1 つで、
     * 保存時に判別コメントを組み立てて両 Leg の memo_jepx へ載せる。
     */
    emissionLinked: z.boolean().optional(),
    emissionAlpha: z.string().optional(),
  })
  .superRefine(emissionRefine)
  .superRefine((data, ctx) => {
    if (data.kind !== 'STANDARD' && data.secondaryLeg === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'リンク / ループ型では Secondary Leg（2本目の入札）を入力してください',
        path: ['secondaryLeg'],
      })
    }

    const validateTimeRange = (
      leg: BlockLegFormInput,
      prefix: string,
    ): void => {
      if (leg.timeCodeTo < leg.timeCodeFrom) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '終了コマは開始コマ以上にしてください',
          path: [prefix, 'timeCodeTo'],
        })
      } else if (leg.timeCodeTo - leg.timeCodeFrom < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '時間帯は連続 4コマ（2時間）以上で設定してください',
          path: [prefix, 'timeCodeTo'],
        })
      }
    }

    /**
     * コマ別上書きの値域を [[blockSlotVolumeViolationOf]] で検証する。
     *
     * 検証対象を **選択中のコマ範囲内**に限るのは、 範囲外の上書きが提出値に
     * 含まれない ([[legToBlockBidInput]]) うえ入力欄も描画されないため
     * ([[BlockLegVolumeSection]])。 範囲外まで検証すると、 画面のどこにも無い
     * コマの値で提出がブロックされ、 利用者が直せないエラーになる。
     */
    const validateSlotVolumes = (
      leg: BlockLegFormInput,
      prefix: string,
    ): void => {
      if (leg.timeCodeTo < leg.timeCodeFrom) return
      for (const [index, slot] of (leg.slotVolumes ?? []).entries()) {
        if (
          slot.timeCode < leg.timeCodeFrom ||
          slot.timeCode > leg.timeCodeTo
        ) {
          continue
        }
        const violation = blockSlotVolumeViolationOf(
          slot.volumeMwh,
          isBoundaryTimeCode(leg, slot.timeCode),
        )
        if (violation === undefined) continue
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: BLOCK_SLOT_VOLUME_VIOLATION_MESSAGES[violation].slot(
            slot.timeCode,
          ),
          path: [prefix, 'slotVolumes', index, 'volumeMwh'],
        })
      }
    }

    validateTimeRange(data.primaryLeg, 'primaryLeg')
    validateSlotVolumes(data.primaryLeg, 'primaryLeg')
    if (data.secondaryLeg !== undefined) {
      validateTimeRange(data.secondaryLeg, 'secondaryLeg')
      validateSlotVolumes(data.secondaryLeg, 'secondaryLeg')
    }
  })
  .refine(...baseVolumeCheck('primaryLeg'))
  .refine(...baseVolumeCheck('secondaryLeg'))

export type BlockBidCreateFormInput = z.infer<typeof blockBidCreateSchema>

const desiredPriceBlockLegFormSchema = blockLegFormSchema.safeExtend({
  // 手数料自動考慮 ON の未入力は「希望価格を入力してください」 (AC-BSP-074 / AC-BSP-004b)
  priceYenPerKwh: z.number({ error: '希望価格を入力してください' }),
})

/**
 * 手数料自動考慮が ON のブロック入札のフォームの schema (AC-BSP-004b)。 価格欄は希望価格なので、 新規作成・編集を
 * 問わず 0.01〜999 円/kWh の範囲を置かず、 保存する入札価格の値域は送信時に判定する (AC-BSP-106、
 * #11466 Q117 = B / Q117-2)。 売りの希望価格 0 は入札価格 0.03、 -0.02 は 0.01 で作れ、 買いの希望価格 0 は
 * 入札価格 -0.03 で値域外になる。 OFF は AC-BSP-050 のとおり [[blockBidCreateSchema]] (0.01〜999) を使う。
 */
export const desiredPriceBlockBidCreateSchema = blockBidCreateSchema.safeExtend(
  {
    primaryLeg: desiredPriceBlockLegFormSchema,
    secondaryLeg: desiredPriceBlockLegFormSchema.optional(),
  },
)

/**
 * コマ別上書きを O(1) 参照できる Map にする ([[effectiveSlotVolume]] を 48 行ぶん
 * 引く描画経路で使う)。
 */
export function slotVolumeMapOf(
  slotVolumes: BlockLegFormInput['slotVolumes'],
): Map<number, number> {
  return new Map((slotVolumes ?? []).map((s) => [s.timeCode, s.volumeMwh]))
}

/**
 * コマ `timeCode` の実効入札量 = コマ別上書きがあればその値、 無ければ全コマ一括値。
 * 2 層モデルの参照規則の唯一の実装 (= 入力欄・提出変換で同じ規則を使う)。
 */
export function effectiveSlotVolume(
  overrides: Map<number, number>,
  timeCode: number,
  baseVolumeMwh: number | undefined,
): number | undefined {
  // 一括値が空で上書きも無いコマは undefined (= 実効値なし)。 [[baseVolumeCheck]] が
  // 提出前に弾き、 表示側は空欄にする。
  return overrides.get(timeCode) ?? baseVolumeMwh
}

/** leg の選択中コマ範囲の 1 コマ分の実効入札量 ([[effectiveLegSlotVolumes]])。 */
export interface EffectiveLegSlotVolume {
  timeCode: number
  /** 実効入札量 (MW)。 上書きも一括値も無いコマと、 有限値でない値 (空欄の NaN) は undefined。 */
  volumeMwh: number | undefined
  /** 一括値ではなくコマ別上書きで値が決まるコマか。 */
  overridden: boolean
}

/**
 * leg の選択中コマ範囲 (timeCodeFrom〜timeCodeTo) の各コマの実効入札量を返す
 * ([[effectiveSlotVolume]] の上書き優先)。 一括値の検証 ([[baseVolumeCheck]])・
 * 上限数量(1札) の判定 ([[maxEffectiveLegVolume]])・提出値の組み立て
 * ([[legToBlockBidInput]]) が同じ列を使う。 範囲が不正 (timeCodeTo < timeCodeFrom)
 * なら空配列。
 */
export function effectiveLegSlotVolumes(
  leg: Pick<
    BlockLegFormInput,
    'volumeMwh' | 'slotVolumes' | 'timeCodeFrom' | 'timeCodeTo'
  >,
): EffectiveLegSlotVolume[] {
  const overrides = slotVolumeMapOf(leg.slotVolumes)
  const slots: EffectiveLegSlotVolume[] = []
  for (
    let timeCode = leg.timeCodeFrom;
    timeCode <= leg.timeCodeTo;
    timeCode++
  ) {
    const effective = effectiveSlotVolume(overrides, timeCode, leg.volumeMwh)
    slots.push({
      timeCode,
      volumeMwh:
        effective !== undefined && Number.isFinite(effective)
          ? effective
          : undefined,
      overridden: overrides.has(timeCode),
    })
  }
  return slots
}

/**
 * 1 コマの実効入札量が 上限数量(1札) (qtyMaxMw) を超えるかを判定する
 * (`bidding/bid-limits.md` §1-2「1 コマの数量が 上限数量(1札) を超えるか」)。
 *
 * この業務規則 (qtyMaxMw が undefined = 制限未設定 or エリア/売買 未確定なら判定しない /
 * 非有限値は判定しない / 境界値ちょうどは違反にしない) の**唯一の実装**。
 * [[BlockLegVolumeSection]] の行エラー表示 (AC-BSP-030a) と
 * [[blockLegExceedsPerBidLimit]] の submit ブロック (AC-BSP-104) の両方がこれを使う。
 * しきい値の向き (`>` / `>=`)・丸め許容を変えるときはここ 1 箇所を直せば両方に効く。
 */
export function slotExceedsQtyMax(
  volumeMw: number,
  qtyMaxMw: number | undefined,
): boolean {
  if (qtyMaxMw === undefined) return false
  return Number.isFinite(volumeMw) && volumeMw > qtyMaxMw
}

/**
 * leg 内の選択中コマ範囲 (timeCodeFrom〜timeCodeTo) の実効入札量の最大値を返す。
 * 範囲が不正 (timeCodeTo < timeCodeFrom) か、 範囲内に有限値が 1 件も無ければ
 * undefined (= 判定不能)。
 */
export function maxEffectiveLegVolume(
  leg: Pick<
    BlockLegFormInput,
    'volumeMwh' | 'slotVolumes' | 'timeCodeFrom' | 'timeCodeTo'
  >,
): number | undefined {
  let max: number | undefined
  for (const { volumeMwh } of effectiveLegSlotVolumes(leg)) {
    if (volumeMwh !== undefined && (max === undefined || volumeMwh > max)) {
      max = volumeMwh
    }
  }
  return max
}

/**
 * leg 内の選択中コマ範囲のいずれかの実効入札量が 上限数量(1札) (qtyMaxMw) を
 * 超えるかを判定する (AC-BSP-104)。 ブロック入札は all-or-nothing (1 コマでも
 * 超過したら札全体が入札不可、 `bidding/bid-limits.md` §1-2)。
 *
 * 判定は行表示と同じ per-slot 述語 ([[slotExceedsQtyMax]]) で、
 * `max(x) > L` ≡ `∃x: x > L` なので leg 内の最大コマ ([[maxEffectiveLegVolume]])
 * に 1 回当てれば「どれか 1 コマでも超過」と等価になる。
 */
export function blockLegExceedsPerBidLimit(
  leg: Pick<
    BlockLegFormInput,
    'volumeMwh' | 'slotVolumes' | 'timeCodeFrom' | 'timeCodeTo'
  >,
  qtyMaxMw: number | undefined,
): boolean {
  const max = maxEffectiveLegVolume(leg)
  return max !== undefined && slotExceedsQtyMax(max, qtyMaxMw)
}
